/**
 * useAriaNudge — al entrar, si hay avisos pendientes, un toast que invita a
 * revisarlos y abre la campana.
 *
 * Sustituye a las tarjetas de señales que flotaban junto al orbe de ARIA Home:
 * los avisos tienen un solo sitio (la campana), y el aviso de bienvenida es lo
 * que lleva hasta él. Lee el mismo `GET /api/aria/inbox` que la campana y el
 * orbe (React Query deduplica → sin peticiones extra).
 *
 * Se muestra UNA vez por sesión y empresa: `sessionStorage` —no `localStorage`—
 * porque "al entrar" es por sesión; y por empresa porque al cambiar de sociedad
 * los avisos son otros.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useApp } from '@/components/hooks/useApp';
import { safeSessionStorage } from '@/lib/safeStorage';
import { useAriaInboxSuggestions } from '@/components/ai/v2/useCockpitData';

const NUDGE_KEY_PREFIX = 'aria_nudge_shown:';

/**
 * Compone el mensaje con los avisos más severos (el backend ya los ordena por
 * severidad, así que los primeros son los que importan).
 *
 * @param {Array<{title?: string}>} suggestions
 * @returns {string}
 */
export function buildNudgeMessage(suggestions) {
  const titles = suggestions.map((s) => s?.title).filter(Boolean);
  if (titles.length === 0) return '';
  if (titles.length === 1) return titles[0];
  if (titles.length === 2) return `${titles[0]} y ${titles[1]}`;
  const rest = titles.length - 2;
  return `${titles[0]}, ${titles[1]} y ${rest} ${rest === 1 ? 'aviso más' : 'avisos más'}`;
}

/**
 * @param {() => void} onOpenBell — abre la campana (acción del toast).
 */
export function useAriaNudge(onOpenBell) {
  const { company } = useApp();
  const { data, isLoading } = useAriaInboxSuggestions(company?.id);
  // El toast es un efecto secundario: sin este guard, cualquier re-render con
  // datos en caché lo volvería a lanzar antes de que sessionStorage se lea.
  const firedRef = useRef(false);
  const onOpenBellRef = useRef(onOpenBell);
  onOpenBellRef.current = onOpenBell;

  useEffect(() => {
    if (isLoading || !company?.id || firedRef.current) return;

    const suggestions = data?.suggestions ?? [];
    if (suggestions.length === 0) return;

    const key = `${NUDGE_KEY_PREFIX}${company.id}`;
    if (safeSessionStorage.getItem(key)) return;

    firedRef.current = true;
    safeSessionStorage.setItem(key, '1');

    toast(`Tienes ${suggestions.length} ${suggestions.length === 1 ? 'aviso' : 'avisos'} por revisar`, {
      description: buildNudgeMessage(suggestions),
      duration: 8000,
      closeButton: true,
      action: {
        label: 'Ver avisos',
        onClick: () => onOpenBellRef.current?.(),
      },
    });
  }, [data, isLoading, company?.id]);
}

export default useAriaNudge;
